/**
 * ImageZoomLightbox — click any thumbnail to open a full-screen viewer with
 * scroll-wheel/pinch zoom, click-drag panning and keyboard nav (←/→/Esc).
 *
 * Usage:
 *   const [zoom, setZoom] = useState(null); // {images: string[], index: 0}
 *   <ImageZoomLightbox state={zoom} onClose={() => setZoom(null)} />
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

export const ImageZoomLightbox = ({ state, onClose }) => {
  const open = !!state;
  const [idx, setIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  useEffect(() => {
    if (open) {
      setIdx(state?.index || 0);
      setScale(1);
      setPos({ x: 0, y: 0 });
    }
  }, [open, state]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(6, s + 0.5));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, state]);

  if (!open || !state?.images?.length) return null;

  const total = state.images.length;
  const cur = state.images[idx];

  const prev = () => {
    setIdx((i) => (i - 1 + total) % total);
    setScale(1);
    setPos({ x: 0, y: 0 });
  };
  const next = () => {
    setIdx((i) => (i + 1) % total);
    setScale(1);
    setPos({ x: 0, y: 0 });
  };
  const zoomIn = () => setScale((s) => Math.min(6, s + 0.5));
  const zoomOut = () => setScale((s) => Math.max(1, s - 0.5));
  const onWheel = (e) => {
    e.preventDefault();
    setScale((s) => Math.min(6, Math.max(1, s + (e.deltaY < 0 ? 0.25 : -0.25))));
  };
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPos({
      x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.origY + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center select-none"
        onClick={onClose}
        data-testid="zoom-lightbox"
      >
        {/* Controls */}
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); zoomOut(); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Zoom out"
            data-testid="zoom-out"
          >
            <ZoomOut size={18} strokeWidth={1.6} />
          </button>
          <span className="text-white/80 text-xs tabnum w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={(e) => { e.stopPropagation(); zoomIn(); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Zoom in"
            data-testid="zoom-in"
          >
            <ZoomIn size={18} strokeWidth={1.6} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
            data-testid="zoom-close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        {/* Counter */}
        {total > 1 && (
          <div className="absolute top-4 left-4 text-white/80 text-sm tabnum bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
            {idx + 1} / {total}
          </div>
        )}

        {/* Prev/Next */}
        {total > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 md:left-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Previous"
              data-testid="zoom-prev"
            >
              <ChevronLeft size={20} strokeWidth={1.6} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 md:right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Next"
              data-testid="zoom-next"
            >
              <ChevronRight size={20} strokeWidth={1.6} />
            </button>
          </>
        )}

        {/* Image */}
        <div
          onClick={(e) => e.stopPropagation()}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="w-full h-full flex items-center justify-center overflow-hidden"
          style={{ cursor: scale > 1 ? (dragRef.current.dragging ? "grabbing" : "grab") : "zoom-in" }}
        >
          <img
            src={cur}
            alt={`Zoomed ${idx + 1}`}
            draggable={false}
            className="max-w-[92vw] max-h-[85vh] object-contain transition-transform duration-100"
            style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageZoomLightbox;
